import * as Previews from "./previews";
import * as Bodies from "./bodies";
import { attachmentKind } from "./frame";

/**
 * The nine Scrapbook post styles (Sam, 2026-09-15). Short Story was left out, and
 * Generations has no link to the Family Tree.
 *
 * Keep the KEYS, photo counts and required boxes in step with STYLES in the
 * backend's flask_app/api/scrapbook/_shared.py. The server is what decides;
 * fitProblem() below only mirrors it so the form can explain itself before the
 * member presses Share.
 */
export const STYLES = [
  { key: "snapshot", label: "Snapshot", blurb: "Photos in an even frame", how: "Add one or more photos. Nothing else to fill in.", Preview: Previews.Snapshot, Body: Bodies.Snapshot },
  { key: "keepsake", label: "Keepsake", blurb: "One photo, shown whole", photos: [1, 1], how: "Add one photo. It is shown whole, and your words go on the print.", Preview: Previews.Keepsake, Body: Bodies.Keepsake },
  {
    key: "then_now", label: "Then & Now", blurb: "Two photos, two years", photos: [2, 2], how: "Add two photos of the same place, then write the year under each.",
    fields: [
      { key: "then_year", label: "Year of the first photo", placeholder: "Then, e.g. 1962", max: 20 },
      { key: "now_year", label: "Year of the second photo", placeholder: "Now, e.g. 2026", max: 20 },
    ],
    Preview: Previews.ThenNow, Body: Bodies.ThenNow,
  },
  { key: "clipping", label: "Clipping", blurb: "Letters, cuttings, PDFs", document: true, how: "Add one letter, cutting or PDF. Its first page becomes the picture.", Preview: Previews.Clipping, Body: Bodies.Clipping },
  // A line per photo (Sam, 2026-09-15), paired by position like Pop-Up Book.
  { key: "album", label: "Album Page", blurb: "Several photos together", photos: [2, 6], lines: 100, how: "Add 2 to 6 photos, then a line for each one if you like.", Preview: Previews.Album, Body: Bodies.Album },
  {
    key: "placard", label: "Museum Placard", blurb: "An heirloom with a label", photos: [1, 1], how: "Add one photo of the object, then its name and how it came down.",
    fields: [
      { key: "object", label: "The object", placeholder: "e.g. Christening cup", max: 80, need: "the object's name" },
      { key: "made", label: "Made of, and when", placeholder: "e.g. Silver, about 1890", max: 80 },
      { key: "story", label: "Its story", placeholder: "A line or two", max: 240, long: true },
      { key: "provenance", label: "How it came down", placeholder: "e.g. Passed down through the family", max: 120 },
      { key: "collection_no", label: "Collection number", placeholder: "Optional", max: 40 },
    ],
    Preview: Previews.Placard, Body: Bodies.Placard,
  },
  {
    // Up to four FILES: the first is the round one, the rest run along the
    // bottom, which turns it into a short family article (Sam, 2026-09-15).
    //
    // "files", not "photos" — this is the one template whose shape does not
    // depend on photographs. It is a fact and its source, and a source is very
    // often a PDF or a document (Sam, 2026-09-16: "did you know cannot be too
    // restirctive what if i wanted pdfs"). Every other template really does
    // need pictures: two frames side by side, a page each, a row of portraits.
    key: "did_you_know", label: "Did You Know?", blurb: "A fact, with its source", files: [1, 4], how: "Add 1 to 4 photos, PDFs or documents, then the fact and where it comes from.",
    fields: [
      { key: "fact", label: "The fact", placeholder: "One surprising line", max: 200, long: true, need: "the fact" },
      { key: "more", label: "Tell me more", placeholder: "Optional, a little more", max: 500, long: true },
      { key: "source", label: "Source", placeholder: "Where this comes from", max: 160, need: "its source" },
    ],
    Preview: Previews.DidYouKnow, Body: Bodies.DidYouKnow,
  },
  {
    key: "popup_book", label: "Pop-Up Book", blurb: "A story in pages", photos: [3, 5], how: "Add 3 to 5 photos, one per page, then a line for each page.",
    fields: [{ key: "title", label: "Title", placeholder: "e.g. The reunion tent, start to finish", max: 80 }],
    pages: 100,
    Preview: Previews.PopUpBook, Body: Bodies.PopUpBook,
  },
  // A line-up: people oldest to youngest, or any set that belongs together —
  // three bouquets, each daughter's pie (Sam, 2026-09-15).
  { key: "generations", label: "Generations", blurb: "A line-up, people or things", photos: [2, 4], people: { name: 60, label: 40 }, how: "Add 2 to 4 photos, oldest or first on the left, then a name and label for each.", Preview: Previews.Generations, Body: Bodies.Generations },
];

export const DEFAULT_STYLE = "snapshot";

/** The style for a key; anything unrecognised is a Snapshot, so no post can blank. */
export function styleFor(key) {
  return STYLES.find((s) => s.key === key) || STYLES[0];
}

/** Why a post does not fit its style yet, in the server's words, or null. */
export function fitProblem(key, attachments, details) {
  const style = styleFor(key);
  const kinds = (attachments || []).map(attachmentKind);
  const photos = kinds.filter((k) => k === "image").length;

  if (style.document) {
    if (kinds.length !== 1 || !["document", "image"].includes(kinds[0])) {
      return `${style.label} needs one letter, cutting or PDF (a scanned photo works too).`;
    }
  } else if (style.files) {
    const [low, high] = style.files;
    if (kinds.length < low || kinds.length > high) {
      return `${style.label} needs ${low} to ${high} files — photos, PDFs or documents.`;
    }
  } else if (style.photos) {
    const [low, high] = style.photos;
    // Say WHICH thing is wrong. "needs exactly two photos" while two files sit
    // on screen reads as a miscount; usually they are simply not photographs.
    if (photos !== kinds.length) {
      const odd = kinds.length - photos;
      return `${style.label} needs photographs, and ${odd === 1 ? "one of those is" : "some of those are"} not a photo. ` +
             `Try Clipping or Did You Know? for a document.`;
    }
    if (photos < low || photos > high) {
      if (low === high) {
        return `${style.label} needs exactly ${low === 1 ? "one photo" : low === 2 ? "two photos" : `${low} photos`}.`;
      }
      return `${style.label} needs ${low} to ${high} photos.`;
    }
  }

  const needs = (style.fields || []).filter((f) => f.need);
  if (needs.some((f) => !String(details?.[f.key] || "").trim())) {
    return `${style.label} needs ${needs.map((f) => f.need).join(" and ")}.`;
  }
  return null;
}
